import { Speciality } from "@mindlercare/schema";
import Checkbox from "components/lib/checkbox";
import { Typography } from "components/typography";
import { PropsWithChildren, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { useGetSpecialities } from "../../api/useSpecialitiesQuery";
import useMarketStrategy from "../../strategies/market/useMarketStrategy";
import { PsychologistProfile } from "../../core/types";

type PsychologistWithSpecialities = Pick<PsychologistProfile, "specialities">;

type SpecialitiesProps = {
  psychologist: PsychologistWithSpecialities;
  onChange: (specialities: number[]) => void;
  error: boolean;
};

const Specialities = ({
  psychologist,
  onChange,
  error,
}: PropsWithChildren<SpecialitiesProps>) => {
  const { t } = useTranslation();
  const {
    specialities: { hasFilteredSpecialities },
  } = useMarketStrategy();
  const { data, isSuccess } = useGetSpecialities();

  const specialities = useMemo(() => {
    if (!isSuccess) return [];

    const filtered = data.filter((speciality: Speciality) =>
      Array.isArray(hasFilteredSpecialities)
        ? !hasFilteredSpecialities.includes(speciality.enum)
        : speciality,
    );

    const translated = filtered.map((speciality: Speciality) => {
      const translatedName = t(speciality.enum.toUpperCase(), "*Unknown");
      return { ...speciality, enum: translatedName };
    });

    translated.sort((a: Speciality, b: Speciality) =>
      a.enum.localeCompare(b.enum),
    );

    return translated;
  }, [isSuccess, data, hasFilteredSpecialities, t]);

  return (
    <SpecialitiesContainer>
      <Typography
        variant="h3"
        color={error ? "ERROR" : undefined}
        style={{ marginBottom: "10px" }}
        insensitive
      >
        {`${t("SPECIALITY_FIELDS")}*`}
      </Typography>
      <div id={"cy-specialities"}>
        {specialities.map((speciality: Speciality) => (
          <SpecialityItem
            {...speciality}
            psychologist={psychologist}
            onSelectedChanged={(specialityId: number) => {
              if (psychologist.specialities?.includes(specialityId)) {
                onChange(
                  psychologist.specialities.filter((id) => id !== specialityId),
                );
              } else {
                onChange([...(psychologist.specialities ?? []), specialityId]);
              }
            }}
            key={`speciality-${speciality.specialityId}`}
          />
        ))}
      </div>
    </SpecialitiesContainer>
  );
};

type SpecialityItemProps = {
  psychologist: PsychologistWithSpecialities;
  onSelectedChanged: (specialityId: number) => void;
} & Speciality;

const SpecialityItem = ({
  enum: translatedEnum,
  specialityId,
  psychologist,
  onSelectedChanged,
}: SpecialityItemProps) => {
  return (
    <SpecialityItemContainer id={`speciality-${specialityId}`}>
      <Typography variant="body" insensitive>
        {translatedEnum}
      </Typography>
      <Checkbox
        value={translatedEnum}
        checked={psychologist.specialities?.includes(specialityId)}
        onChange={() => onSelectedChanged(specialityId)}
      />
    </SpecialityItemContainer>
  );
};

const SpecialitiesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  & > div {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 32px;
  }
`;

const SpecialityItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ceccc4;

  & p {
    margin: 16px 0;
  }
`;

export default Specialities;
